import React, { useMemo, useState } from "react";
import { IonLabel, IonSegment, IonSegmentButton } from "@ionic/react";

type LanguageTab = "general" | "chrome" | "edge" | "firefox" | "safari";

const tabOptions: { id: LanguageTab; label: string }[] = [
  { id: "general", label: "General" },
  { id: "chrome", label: "Chrome" },
  { id: "edge", label: "Edge" },
  { id: "firefox", label: "Firefox" },
  { id: "safari", label: "Safari" },
];

const detectBrowserTab = (): LanguageTab => {
  if (typeof navigator === "undefined") {
    return "general";
  }

  const userAgent = navigator.userAgent;
  if (/Edg\//.test(userAgent)) {
    return "edge";
  }
  if (/Firefox\//.test(userAgent)) {
    return "firefox";
  }
  if (/Chrome\//.test(userAgent)) {
    return "chrome";
  }
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent) && !/Edg\//.test(userAgent)) {
    return "safari";
  }

  return "general";
};

const instructionsByTab: Record<
  LanguageTab,
  { title: string; bullets: string[]; linkUrl?: string; linkLabel?: string }
> = {
  general: {
    title: "General instructions",
    bullets: [
      "Right-click the page and choose Translate if available.",
      "If you do not see Translate, open the browser menu and look for Translate.",
      "Change the target language if the default is not correct.",
    ],
  },
  chrome: {
    title: "Chrome instructions",
    bullets: [
      "When Chrome detects another language, use the translate icon in the address bar.",
      "You can also right-click the page and choose Translate to [language].",
      "Use the translate popup to switch the target language if needed.",
    ],
    linkUrl: "https://support.google.com/chrome/answer/173424",
    linkLabel: "Chrome: Translate pages and change languages",
  },
  edge: {
    title: "Edge instructions",
    bullets: [
      "When Edge detects another language, click the translate icon in the address bar.",
      "You can also right-click the page and choose Translate.",
      "Use the translate panel to pick a different target language if needed.",
    ],
    linkUrl: "https://support.microsoft.com/en-us/topic/use-microsoft-translator-in-microsoft-edge-browser-4ad1c6cb-01a4-4227-be9d-a81e127fcb0b",
    linkLabel: "Edge: Use Microsoft Translator in Edge",
  },
  firefox: {
    title: "Firefox instructions",
    bullets: [
      "Firefox shows a translation panel for pages in other languages.",
      "Click the translate icon or use the menu to translate the page.",
      "Choose the target language in the panel if the default is not correct.",
    ],
    linkUrl: "https://support.mozilla.org/en-US/kb/website-translation",
    linkLabel: "Firefox: Translate websites",
  },
  safari: {
    title: "Safari instructions",
    bullets: [
      "If available, click the translate icon or the aA Page Settings button in the address bar.",
      "Choose Translate to [language].",
      "If Translate is missing, the page or language may not be supported.",
    ],
    linkUrl: "https://support.apple.com/guide/safari/translate-webpages-ibrw646b2ca2/mac",
    linkLabel: "Safari: Translate webpages",
  },
};

const LanguageHelpPopover: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LanguageTab>(() => detectBrowserTab());
  const activeInstructions = useMemo(
    () => instructionsByTab[activeTab],
    [activeTab]
  );

  return (
    <div className="language-help-content">
      <div className="language-help-header">
        <h2>Want a different language?</h2>
        <p>Use your browser&apos;s built-in translation tools.</p>
      </div>
      <IonSegment
        value={activeTab}
        onIonChange={(event) => setActiveTab(event.detail.value as LanguageTab)}
        scrollable={true}
        className="language-help-segment"
      >
        {tabOptions.map((tab) => (
          <IonSegmentButton key={tab.id} value={tab.id}>
            <IonLabel>{tab.label}</IonLabel>
          </IonSegmentButton>
        ))}
      </IonSegment>
      <div className="language-help-body">
        <h3>{activeInstructions.title}</h3>
        <ul>
          {activeInstructions.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
        {activeInstructions.linkUrl && activeInstructions.linkLabel && (
          <p>
            <a
              href={activeInstructions.linkUrl}
              target="_blank"
              rel="noreferrer"
            >
              {activeInstructions.linkLabel}
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default LanguageHelpPopover;
